import React, { useEffect, useRef, useState } from 'react';
import { sanitize, toHtml } from '../../utils/richText';

// Small contenteditable editor. Built on execCommand rather than a library: the
// client has no UI dependencies at all, and pulling in Quill or TipTap for bold,
// italic and a colour swatch would outweigh the feature. execCommand is formally
// deprecated but is implemented everywhere and has no replacement for this job.
//
// The markup it produces is whatever the browser emits, so it is sanitised on the
// way out here and again on the server — see utils/richText.js.

const COLORS = [
  { name: 'Default', value: '#1A1A2E' },
  { name: 'Red',     value: '#C00000' },
  { name: 'Orange',  value: '#E67E22' },
  { name: 'Green',   value: '#107C41' },
  { name: 'Blue',    value: '#2E75B6' },
  { name: 'Navy',    value: '#1A3C6E' },
  { name: 'Purple',  value: '#7030A0' },
  { name: 'Grey',    value: '#7F7F7F' },
];

const BUTTONS = [
  { cmd: 'bold',          label: 'B', title: 'Bold (Ctrl+B)',      style: { fontWeight: 800 } },
  { cmd: 'italic',        label: 'I', title: 'Italic (Ctrl+I)',    style: { fontStyle: 'italic' } },
  { cmd: 'underline',     label: 'U', title: 'Underline (Ctrl+U)', style: { textDecoration: 'underline' } },
  { cmd: 'strikeThrough', label: 'S', title: 'Strikethrough',      style: { textDecoration: 'line-through' } },
];

// `compact` is for use inside a table row: no label, tighter padding, and the
// toolbar floats above the field only while it has focus, so ten line items do
// not mean ten toolbars — and showing it does not shift the row.
export default function RichText({ label, value, onChange, placeholder, minHeight = 90, compact = false }) {
  const ref = useRef(null);
  const [focused, setFocused] = useState(false);
  const [palette, setPalette] = useState(false);

  // Only push into the DOM when the incoming value differs from what is already
  // rendered. Writing on every keystroke would reset the caret to the start.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const next = toHtml(value);
    if (el.innerHTML !== next) el.innerHTML = next;
  }, [value]);

  const emit = () => {
    const el = ref.current;
    if (el) onChange(sanitize(el.innerHTML));
  };

  // styleWithCSS decides whether the browser emits tags or inline styles, and the
  // whitelist wants a different answer per command: with it off, foreColor emits
  // <font color> (dropped, so the colour vanishes); with it on, bold emits
  // <span style="font-weight:bold"> (style dropped, so the bold vanishes).
  // Colour needs CSS, everything else needs tags.
  const exec = (cmd, arg) => {
    ref.current?.focus();
    document.execCommand('styleWithCSS', false, cmd === 'foreColor');
    document.execCommand(cmd, false, arg);
    emit();
  };

  // Paste as plain text — a copy from Word or a web page otherwise drags in fonts,
  // sizes and background colours that the whitelist strips anyway, which looks
  // like the paste silently failed.
  const onPaste = e => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text/plain');
    document.execCommand('insertText', false, text);
    emit();
  };

  const showPlaceholder = !focused && !toHtml(value);
  const showToolbar = !compact || focused || palette;

  const toolbarStyle = compact
    ? {
        display: 'flex', alignItems: 'center', gap: 2, padding: '4px 5px',
        flexWrap: 'nowrap', position: 'absolute', bottom: '100%', left: 0, marginBottom: 4,
        background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8,
        boxShadow: 'var(--shadow)', zIndex: 12, whiteSpace: 'nowrap',
      }
    : {
        display: 'flex', alignItems: 'center', gap: 2, padding: '5px 6px',
        borderBottom: '1px solid var(--border-light)', flexWrap: 'wrap', position: 'relative',
      };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, position: compact ? 'relative' : 'static' }}>
      {label && <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)' }}>{label}</label>}

      <div style={{ border: `1px solid ${focused ? 'var(--blue)' : 'var(--border)'}`, borderRadius: compact ? 6 : 8, overflow: 'visible', background: 'var(--input-bg)', position: 'relative' }}>
        {/* Toolbar */}
        {showToolbar && (
        <div style={toolbarStyle}>
          {BUTTONS.map(b => (
            <button key={b.cmd} type="button" title={b.title}
              onMouseDown={e => e.preventDefault()}
              onClick={() => exec(b.cmd)}
              style={{ ...toolBtn, ...b.style }}>
              {b.label}
            </button>
          ))}
          <Divider />
          <button type="button" title="Bulleted list"
            onMouseDown={e => e.preventDefault()} onClick={() => exec('insertUnorderedList')}
            style={toolBtn}>• ≡</button>
          <button type="button" title="Numbered list"
            onMouseDown={e => e.preventDefault()} onClick={() => exec('insertOrderedList')}
            style={toolBtn}>1. ≡</button>
          <Divider />
          <div style={{ position: 'relative' }}>
            <button type="button" title="Text colour"
              onMouseDown={e => e.preventDefault()} onClick={() => setPalette(p => !p)}
              style={{ ...toolBtn, display: 'flex', alignItems: 'center', gap: 3 }}>
              <span style={{ fontWeight: 700 }}>A</span>
              <span style={{ width: 11, height: 11, borderRadius: 2, background: 'linear-gradient(90deg,#C00000,#2E75B6)', display: 'inline-block' }} />
            </button>
            {palette && (
              <>
                {/* Click-away catcher */}
                <div onClick={() => setPalette(false)}
                  style={{ position: 'fixed', inset: 0, zIndex: 10 }} />
                <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 11, background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 8, boxShadow: 'var(--shadow)', display: 'grid', gridTemplateColumns: 'repeat(4, 22px)', gap: 6 }}>
                  {COLORS.map(c => (
                    <button key={c.value} type="button" title={c.name}
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => { exec('foreColor', c.value); setPalette(false); }}
                      style={{ width: 22, height: 22, borderRadius: 4, background: c.value, border: '1px solid var(--border)', cursor: 'pointer', padding: 0 }} />
                  ))}
                </div>
              </>
            )}
          </div>
          <Divider />
          <button type="button" title="Remove formatting"
            onMouseDown={e => e.preventDefault()} onClick={() => exec('removeFormat')}
            style={{ ...toolBtn, fontSize: 12 }}>✕ᴀ</button>
        </div>
        )}

        {/* Editable area */}
        <div style={{ position: 'relative' }}>
          <div ref={ref} contentEditable suppressContentEditableWarning
            onInput={emit} onBlur={() => { setFocused(false); emit(); }}
            onFocus={() => setFocused(true)} onPaste={onPaste}
            style={{ minHeight: compact ? 'auto' : minHeight, padding: compact ? '8px' : '9px 12px', fontSize: compact ? 13 : 14, lineHeight: compact ? 1.4 : 1.6, outline: 'none', color: 'var(--text)', fontFamily: 'inherit', overflowWrap: 'anywhere' }} />
          {showPlaceholder && (
            <div style={{ position: 'absolute', top: compact ? 8 : 9, left: compact ? 8 : 12, fontSize: compact ? 13 : 14, color: 'var(--text-subtle)', pointerEvents: 'none' }}>
              {placeholder}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Divider() {
  return <span style={{ width: 1, height: 18, background: 'var(--border-light)', margin: '0 4px' }} />;
}

const toolBtn = {
  minWidth: 26, height: 26, padding: '0 6px', borderRadius: 5,
  border: '1px solid transparent', background: 'transparent',
  color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer',
  fontFamily: 'inherit', lineHeight: 1,
};
